import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { collection, getDocs, addDoc } from "firebase/firestore";
import { db } from "../firebaseConfig.js";
import Header from "../components/Header.js";
import BookingFeedback from "../components/BookingFeedback.js";
import {
  isValidBhutanMobile,
  isFutureDateTime,
  isWithinBusinessHours,
  formatBhutanPhone,
  formatTimeWithAmPm,
  generateTimeOptions,
} from "../utils/bookingValidation.js";
import { sendSMSToClient } from "../utils/smsUtils.js";
import { submitBookingFeedback } from "../utils/bookingFeedbackUtils.js";

function BookingPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastBookingData, setLastBookingData] = useState(null);
  const [formData, setFormData] = useState({
    service: "",
    subService: "",
    price: "",
    benefits: "",
    duration: "",
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    attendees: "1",
  });

  const [servicesData, setServicesData] = useState([]);
  const [availableSubServices, setAvailableSubServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      const servicesCollection = collection(db, "servicesData");
      const servicesSnapshot = await getDocs(servicesCollection);
      const servicesList = servicesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setServicesData(servicesList);
    };
    fetchServices();
  }, []);

  useEffect(() => {
    const service = searchParams.get("service") || "";
    const subService = searchParams.get("subService") || "";
    const price = searchParams.get("price") || "";
    const selectedService = servicesData.find((item) => item.name === service);
    const selectedSubService = selectedService?.subServices.find(
      (sub) => sub.title === subService
    );
    setFormData((prevData) => ({
      ...prevData,
      service,
      subService,
      price,
      benefits: selectedSubService ? selectedSubService.benefits || "" : "",
      duration:
        selectedSubService && selectedSubService.duration !== "N/A"
          ? selectedSubService.duration
          : "",
    }));
    setAvailableSubServices(selectedService ? selectedService.subServices : []);
  }, [searchParams, servicesData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "service") {
      const selectedService = servicesData.find((item) => item.name === value);
      setAvailableSubServices(
        selectedService ? selectedService.subServices : []
      );
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        subService: "",
        price: "",
        benefits: "",
        duration: "",
      }));
    } else if (name === "subService") {
      const selectedSubService = availableSubServices.find(
        (sub) => sub.title === value
      );
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        price: selectedSubService ? selectedSubService.price : "",
        benefits: selectedSubService ? selectedSubService.benefits || "" : "",
        duration: selectedSubService ? selectedSubService.duration : "",
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!isValidBhutanMobile(formData.phone)) {
      alert(
        "Please enter a valid Bhutan mobile number (starting with 17 or 77, 8 digits)."
      );
      setLoading(false);
      return;
    }

    if (!isFutureDateTime(formData.date, formData.time)) {
      alert("Please select a future date and time for your booking.");
      setLoading(false);
      return;
    }

    if (!isWithinBusinessHours(formData.time)) {
      alert(
        "Our business is open from 10:00 AM to 8:00 PM. Please select a time within our opening hours."
      );
      setLoading(false);
      return;
    }

    const formattedPhone = formatBhutanPhone(formData.phone);
    const formattedTime = formatTimeWithAmPm(formData.time);

    const bookingData = {
      ...formData,
      phone: formattedPhone,
      time: formattedTime,
    };

    try {
      const bookingsCollection = collection(db, "bookings");
      await addDoc(bookingsCollection, bookingData);

      try {
        await sendSMSToClient(bookingData);
        setLastBookingData(bookingData);
        setConfirmation(true);
      } catch (smsError) {
        setLastBookingData(bookingData);
        setConfirmation(true);
        // Still show confirmation even if SMS fails
        console.warn("SMS notification failed:", smsError);
      }

      setFormData({
        service: "",
        subService: "",
        price: "",
        benefits: "",
        duration: "",
        name: "",
        email: "",
        phone: "",
        date: "",
        time: "",
        attendees: "1",
      });
    } catch (error) {
      console.error("Error submitting booking:", error);
      alert("There was an error submitting your booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const timeOptions = generateTimeOptions();

  const handleCloseConfirmation = () => {
    setConfirmation(false);
    // Show feedback modal after closing confirmation
    setTimeout(() => {
      setShowFeedback(true);
    }, 300);
  };

  const handleFeedbackSubmission = async (isPositive) => {
    try {
      await submitBookingFeedback(isPositive, lastBookingData);
      console.log(`Feedback submitted: ${isPositive ? 'Positive' : 'Negative'}`);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      // Don't show error to user, just log it
    }
  };

  const handleCloseFeedback = () => {
    setShowFeedback(false);
    setLastBookingData(null);
  };

  return (
    <>
      <Header />
      {loading && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white bg-opacity-70">
          <div className="animate-spin rounded-full h-16 w-16 border-8 border-blue-200 border-t-blue-600"></div>
          <div className="mt-6 text-blue-700 text-lg font-semibold">
            Submitting your booking...
          </div>
        </div>
      )}
      <div className="booking-page min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Book Your Service
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Schedule your appointment with our traditional healing services.
              Fill out the form below and we'll confirm your booking.
            </p>
          </div>

          {confirmation && (
            <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white bg-opacity-80">
              <div className="bg-white rounded-full shadow-lg p-6 mb-4">
                <svg
                  className="h-16 w-16 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="white"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="3"
                    d="M7 13l3 3 5-5"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-green-700 mb-2">
                Booking Confirmed!
              </h2>
              <p className="text-lg text-gray-700 text-center max-w-md mb-4">
                Thank you for your booking.
                <br />
                You should receive a confirmation SMS shortly.
                <br />
                If you do not receive a message, please contact us.
              </p>
              <button
                className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold shadow hover:bg-blue-700 transition"
                onClick={handleCloseConfirmation}
              >
                Close
              </button>
            </div>
          )}

          {/* Booking Feedback Modal */}
          <BookingFeedback
            isVisible={showFeedback}
            onClose={handleCloseFeedback}
            onFeedback={handleFeedbackSubmission}
          />

          {/* Form Container */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
              {/* Form Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
                <h2 className="text-2xl font-semibold text-white">
                  Booking Information
                </h2>
                <p className="text-blue-100 mt-1">
                  Please provide your details and preferred appointment time
                </p>
              </div>

              {/* Form Content */}
              <form className="p-8 space-y-6" onSubmit={handleSubmit}>
                {/* Service Selection Section */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                    Service Selection
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label
                        htmlFor="service"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Service *
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                        required
                      >
                        <option value="">Select a service</option>
                        {servicesData.map((service, index) => (
                          <option key={index} value={service.name}>
                            {service.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="subService"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Sub-Service *
                      </label>
                      <select
                        id="subService"
                        name="subService"
                        value={formData.subService}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        required
                        disabled={!formData.service}
                      >
                        <option value="">Select a sub-service</option>
                        {availableSubServices.map((subService, index) => (
                          <option key={index} value={subService.title}>
                            {subService.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Service Details */}
                  {(formData.price || formData.duration) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {formData.price && (
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Price
                          </label>
                          <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 font-medium">
                            {formData.price}
                          </div>
                        </div>
                      )}
                      {formData.duration && (
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Duration
                          </label>
                          <div className="px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-800 font-medium">
                            {formData.duration}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Personal Information Section */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                    Personal Information
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                      placeholder="Enter your phone number (17XXXXXX or 77XXXXXX)"
                      required
                    />
                    <p className="text-sm text-gray-500">
                      Please enter a valid Bhutan mobile number (starting with
                      17 or 77, 8 digits)
                    </p>
                  </div>
                </div>

                {/* Appointment Details Section */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                    Appointment Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label
                        htmlFor="date"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Preferred Date *
                      </label>
                      <input
                        type="date"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="time"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Preferred Time *
                      </label>
                      <select
                        id="time"
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                        required
                      >
                        <option value="">Select a time</option>
                        {timeOptions.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="attendees"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Number of People *
                      </label>
                      <input
                        type="number"
                        id="attendees"
                        name="attendees"
                        value={formData.attendees}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                        min="1"
                        max="10"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-300 shadow-lg"
                  >
                    Submit Booking
                  </button>
                </div>

                {/* Additional Info */}
                <div className="bg-gray-50 rounded-lg p-4 mt-6">
                  <p className="text-sm text-gray-600 text-center">
                    <strong>Note:</strong> You will receive a confirmation SMS
                    once your booking is processed. Our team will contact you if
                    any changes are needed.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BookingPage;
